import { useState } from 'react';
import { Permission, SPACE_TAG_MAX, SPACE_TAG_PATTERN, TAG_BADGES, type SelfUser, type TagBadge } from '@spawnwire/shared';
import { Avatar, Button, clsx, Field, Input } from '../../components/ui';
import { http } from '../../lib/api';
import { attempt } from '../../lib/data';
import { NameText } from '../../lib/nameFonts';
import { can, useSpacePermissions } from '../../lib/perms';
import { actions, useStore } from '../../store/store';
import { TAG_BADGE_META, TagChip } from '../members/SpaceTag';
import { SpaceIcon } from '../shell/SpaceIcon';
import { Section } from './SettingsLayout';

function tagError(text: string): string | null {
  if (!text) return null;
  if (text.length > SPACE_TAG_MAX) return `Tags can have at most ${SPACE_TAG_MAX} characters.`;
  if (!SPACE_TAG_PATTERN.test(text)) return 'Use letters, numbers and a few symbols like ! # & * - . / \\ ? @ _ ~, with at least one letter or number.';
  return null;
}

/** Space settings → Space tag: a community picks the short tag its members can wear. */
export function SpaceTagSettings({ spaceId }: { spaceId: string }) {
  const space = useStore((s) => s.spaces[spaceId]!);
  const me = useStore((s) => s.me);
  const editable = can(useSpacePermissions(spaceId), Permission.MANAGE_SPACE);
  const [text, setText] = useState(space.tag?.text ?? '');
  const [badge, setBadge] = useState<TagBadge>(space.tag?.badge ?? 'bolt');
  const trimmed = text.trim();
  const error = tagError(trimmed);
  const dirty = trimmed !== (space.tag?.text ?? '') || badge !== (space.tag?.badge ?? 'bolt');

  if (space.kind !== 'community') {
    return (
      <Section description="Only communities can have a tag. The owner can turn this space into a community under Discovery.">
        <div className="flex items-center gap-2 text-sm text-muted">
          Example: <span className="font-semibold text-fg">{me?.displayName ?? 'You'}</span> <TagChip tag={{ text: 'TAG', badge: 'bolt' }} />
        </div>
      </Section>
    );
  }

  return (
    <>
      <Section description={`A short tag of up to ${SPACE_TAG_MAX} characters. Members can choose to wear it next to their name everywhere on Spawnwire, and anyone can click it to see this space.`}>
        <div className="flex items-start gap-3 rounded-lg border border-line bg-main p-3">
          <Avatar user={me} size={40} />
          <div className="min-w-0">
            <div className="flex items-center gap-1.5">
              <span className="font-semibold text-fg">
                <NameText font={me?.nameFont}>{me?.displayName ?? 'You'}</NameText>
              </span>
              {trimmed && !error ? <TagChip tag={{ text: trimmed, badge }} /> : <TagChip tag={{ text: '····', badge }} className="opacity-50" />}
              <span className="text-xs text-faint">Today</span>
            </div>
            <p className="text-sm text-fg">This is how the tag looks in chat.</p>
          </div>
        </div>
        <Field label="Tag" htmlFor="tag-text" error={error} hint="Letters, numbers and a few symbols. Case is kept as you type it.">
          <Input id="tag-text" value={text} onChange={(e) => setText(e.target.value)} maxLength={SPACE_TAG_MAX + 2} disabled={!editable} placeholder="GG" autoComplete="off" spellCheck={false} className="max-w-40 font-mono" />
        </Field>
        <Field label="Badge">
          <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Badge">
            {TAG_BADGES.map((b) => {
              const { icon: Icon, color, label } = TAG_BADGE_META[b];
              return (
                <button
                  key={b}
                  type="button"
                  role="radio"
                  aria-checked={badge === b}
                  aria-label={label}
                  title={label}
                  disabled={!editable}
                  onClick={() => setBadge(b)}
                  className={clsx('flex size-10 items-center justify-center rounded-lg border-2 bg-sidebar transition-colors disabled:opacity-60', badge === b ? 'border-accent' : 'border-transparent hover:bg-hover')}
                >
                  <Icon className="size-5" style={{ color }} fill={color} strokeWidth={1.5} aria-hidden />
                </button>
              );
            })}
          </div>
        </Field>
        {editable && (
          <div className="flex flex-wrap gap-2">
            <Button disabled={!dirty || !trimmed || !!error} onClick={() => void attempt(() => http.put(`/spaces/${spaceId}/tag`, { text: trimmed, badge }), 'Tag saved.')}>
              Save tag
            </Button>
            {space.tag && (
              <Button
                variant="ghost"
                onClick={() =>
                  void attempt(async () => {
                    await http.del(`/spaces/${spaceId}/tag`);
                    setText('');
                  }, 'Tag removed. Nobody wears it any more.')
                }
              >
                Remove tag
              </Button>
            )}
          </div>
        )}
      </Section>
    </>
  );
}

/** User settings → Profile: which space tag to wear, from the communities you are in that have one. */
export function WornTagPicker() {
  const me = useStore((s) => s.me);
  const spaces = useStore((s) => s.spaces);
  const [busy, setBusy] = useState<string | null>(null);
  const tagged = Object.values(spaces)
    .filter((s) => s.kind === 'community' && s.tag)
    .sort((a, b) => a.name.localeCompare(b.name));
  const current = me?.tag?.spaceId ?? null;

  const wear = (spaceId: string | null) => {
    setBusy(spaceId ?? 'none');
    void attempt(async () => {
      actions.setMe(await http.put<SelfUser>('/users/@me/tag', { spaceId }));
    }).finally(() => setBusy(null));
  };

  return (
    <Field label="Space tag" hint={tagged.length ? 'Shown next to your name in chat, the member list and your profile. Saved as soon as you pick one.' : undefined}>
      {tagged.length === 0 ? (
        <p className="text-sm text-muted">None of your communities has a tag yet. When one does, you can wear it here.</p>
      ) : (
        <div className="space-y-1" role="radiogroup" aria-label="Space tag">
          {tagged.map((s) => (
            <button
              key={s.id}
              type="button"
              role="radio"
              aria-checked={current === s.id}
              disabled={busy !== null}
              onClick={() => wear(current === s.id ? null : s.id)}
              className={clsx('flex w-full items-center gap-3 rounded-lg border-2 px-3 py-2 text-left transition-colors', current === s.id ? 'border-accent bg-accent-soft' : 'border-transparent bg-sidebar hover:bg-hover')}
            >
              <SpaceIcon name={s.name} iconUrl={s.iconUrl} size={28} active />
              <span className="min-w-0 flex-1 truncate text-sm font-medium text-fg">{s.name}</span>
              <TagChip tag={s.tag!} />
            </button>
          ))}
          {current && (
            <Button variant="ghost" size="sm" disabled={busy !== null} onClick={() => wear(null)}>
              Don't wear a tag
            </Button>
          )}
        </div>
      )}
    </Field>
  );
}
